import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, BellRing, Building2, Cog, RefreshCw, AlertTriangle, Copy, CheckCircle2, XCircle, Receipt, LayoutGrid } from "lucide-react";
import useWebPush from "@/hooks/useWebPush";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, "0")}:00`,
}));

const Section = ({ title, subtitle, icon: Icon, children, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 md:p-7" data-testid={testId}>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-11 h-11 rounded-xl bg-[#00f0ff]/10 border border-[#00f0ff]/25 flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-[#00f0ff]" />
      </div>
      <div>
        <div className="font-display font-bold text-lg">{title}</div>
        {subtitle && <div className="text-[#a395c3] text-sm mt-0.5">{subtitle}</div>}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Row = ({ label, hint, children }) => (
  <div className="grid md:grid-cols-[220px_1fr] gap-2 md:gap-6 md:items-center">
    <div>
      <div className="text-sm font-medium text-white/90">{label}</div>
      {hint && <div className="text-xs text-white/50 mt-0.5">{hint}</div>}
    </div>
    <div>{children}</div>
  </div>
);

const NumberSelect = ({ value, onChange, options, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(parseInt(e.target.value, 10))}
    data-testid={testId}
    className="px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#00f0ff]/50 min-w-[120px]"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value} className="bg-[#0a0510]">
        {o.label}
      </option>
    ))}
  </select>
);

const Input = ({ value, onChange, testId, placeholder }) => (
  <input
    type="text"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    className="w-full px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#00f0ff]/50"
  />
);

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/settings`, { headers: authHeaders() });
      setData(res.settings);
      setMeta(res.meta);
      setDirty({});
    } catch (e) {
      toast.error("Failed to load settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const merged = { ...(data || {}), ...dirty };
  const hasChanges = Object.keys(dirty).length > 0;

  const set = (k, v) => setDirty((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await axios.patch(`${API}/admin/settings`, dirty, { headers: authHeaders() });
      setData(res.settings);
      setDirty({});
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const quietOff = merged.push_quiet_hours_start === merged.push_quiet_hours_end;

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full bg-[#00f0ff]/8 blur-[120px]" />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 md:px-10 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <Link to="/admin" data-testid="settings-back" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={14} /> Command Center
            </Link>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
              <Cog size={40} className="inline mr-3 mb-1 text-[#00f0ff]" strokeWidth={1.6} />
              Settings.
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              Live changes — no redeploy needed.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start md:self-auto">
            <button
              onClick={load}
              disabled={loading}
              data-testid="settings-refresh"
              className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center disabled:opacity-50"
              aria-label="Refresh"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
            <button
              onClick={save}
              disabled={saving || !hasChanges}
              data-testid="settings-save"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-40 ${
                hasChanges ? "bg-[#00f0ff] text-[#05050a] hover:bg-white" : "bg-white/[0.05] border border-white/15 text-white/60"
              }`}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {hasChanges ? `Save (${Object.keys(dirty).length})` : "Saved"}
            </button>
          </div>
        </div>

        {loading || !data ? (
          <div className="flex items-center gap-3 text-white/60"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-6">
            <Section
              title="Notifications"
              subtitle="Silent hours + timezone control. Test-send always fires (bypasses quiet hours)."
              icon={BellRing}
              testId="settings-notifications"
            >
              <Row label="Quiet hours" hint="Muted for non-urgent pings. Booking + Spotlight always break through.">
                <div className="flex items-center gap-2 flex-wrap">
                  <NumberSelect value={merged.push_quiet_hours_start} onChange={(v) => set("push_quiet_hours_start", v)} options={HOURS} testId="quiet-start" />
                  <span className="text-white/40 text-xs">to</span>
                  <NumberSelect value={merged.push_quiet_hours_end} onChange={(v) => set("push_quiet_hours_end", v)} options={HOURS} testId="quiet-end" />
                  <button
                    type="button"
                    onClick={() => { set("push_quiet_hours_start", 0); set("push_quiet_hours_end", 0); }}
                    data-testid="quiet-off"
                    className="ml-2 text-xs text-white/50 underline hover:text-white/80"
                  >
                    Turn off
                  </button>
                </div>
                <div className="mt-2 text-xs">
                  {quietOff ? (
                    <span className="text-[#22c55e]">Quiet hours off — notifications ping any time.</span>
                  ) : (
                    <span className="text-white/50">Muted from {String(merged.push_quiet_hours_start).padStart(2, "0")}:00 to {String(merged.push_quiet_hours_end).padStart(2, "0")}:00 daily.</span>
                  )}
                </div>
              </Row>
              <Row label="Timezone">
                <select
                  value={merged.push_timezone}
                  onChange={(e) => set("push_timezone", e.target.value)}
                  data-testid="tz-select"
                  className="px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#00f0ff]/50"
                >
                  {(meta?.timezones || []).map((tz) => (
                    <option key={tz} value={tz} className="bg-[#0a0510]">{tz}</option>
                  ))}
                </select>
              </Row>
              <div className="pt-3 mt-3 border-t border-white/10">
                <PushDiagnostic />
              </div>
            </Section>

            <Section title="Studio" subtitle="Physical address + calendar defaults." icon={Building2} testId="settings-studio">
              <Row label="Studio address" hint="Appears in iCal event locations + admin emails.">
                <Input value={merged.studio_address} onChange={(v) => set("studio_address", v)} testId="studio-address" placeholder="SB Media Studio, Lakeland FL" />
              </Row>
              <Row label="Podcast slots" hint="Comma-separated 24h times (HH:MM). Applied to Spotlight Saturday slot picker.">
                <Input value={merged.podcast_slot_times} onChange={(v) => set("podcast_slot_times", v)} testId="slot-times" placeholder="12:00,17:00" />
              </Row>
            </Section>

            <Section title="Integrations" subtitle="How new leads route." icon={AlertTriangle} testId="settings-integrations">
              <Row label="CRM mode" hint="'email' = notifications only (no Monday writes). 'monday' = push to Monday board on every booking.">
                <select
                  value={merged.crm_mode}
                  onChange={(e) => set("crm_mode", e.target.value)}
                  data-testid="crm-mode"
                  className="px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#00f0ff]/50"
                >
                  {(meta?.crm_modes || []).map((m) => (
                    <option key={m} value={m} className="bg-[#0a0510]">{m}</option>
                  ))}
                </select>
              </Row>
            </Section>

            <Section
              title="Billing defaults"
              subtitle="What auto-invoices go out when a client moves into an active column."
              icon={Receipt}
              testId="settings-billing"
            >
              <Row
                label="Welcome invoice package"
                hint="Auto-issued invoice amount when a new client is approved. Change any time — takes effect on the next approval."
              >
                <select
                  value={merged.default_invoice_package || "studio_day"}
                  onChange={(e) => set("default_invoice_package", e.target.value)}
                  data-testid="default-invoice-package"
                  className="w-full md:w-auto px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#00f0ff]/50"
                >
                  {(meta?.invoice_packages || []).map((p) => (
                    <option key={p.key} value={p.key} className="bg-[#0a0510]">
                      {p.name} — ${(p.amount_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })}
                      {p.recurring ? "/mo" : ""}
                    </option>
                  ))}
                </select>
              </Row>
            </Section>

            <LayoutResetSection />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------- Layout reset ----------------------------
// Wipes both backend `dashboard_prefs` and the localStorage
// `sb_admin_card_open:*` keys so every collapsible accordion returns to
// its component-level default. Handy when an admin's saved layout has
// drifted and they'd rather start over than hunt each toggle.
const LayoutResetSection = () => {
  const [resetting, setResetting] = useState(false);

  const resetAll = async () => {
    if (!window.confirm("Reset all saved layout preferences? This reopens every collapsed card and clears the AdminHub order/hide state on every device you use.")) return;
    setResetting(true);
    try {
      await axios.delete(`${API}/admin/dashboard/prefs`, { headers: authHeaders() });
      // Nuke every card-collapse key we've saved locally. Scoped
      // narrowly so unrelated localStorage entries (auth token, etc.)
      // are untouched.
      try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith("sb_admin_card_open:")) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
      } catch { /* storage unavailable — backend reset still applies */ }
      toast.success("Layout reset. Reload any admin page to see the defaults.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Section
      title="Layout preferences"
      subtitle="Card order, hidden cards, and collapse states sync across every device you're signed in on."
      icon={LayoutGrid}
      testId="settings-layout"
    >
      <Row
        label="Reset layout defaults"
        hint="Restores AdminHub order, un-hides every card, and reopens every collapsible section on this device — and every other device once you sign in there."
      >
        <button
          onClick={resetAll}
          disabled={resetting}
          data-testid="reset-layout-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/15 hover:bg-[#00f0ff]/10 hover:border-[#00f0ff]/40 hover:text-[#00f0ff] text-sm text-white/85 transition-colors disabled:opacity-50"
        >
          {resetting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Reset defaults
        </button>
      </Row>
    </Section>
  );
};

// ---------------------------- Push diagnostic panel ----------------------------
// Rich, verbose panel used to hunt down why a "Send test" ping isn't landing.
// Shows every piece of state we can inspect without server logs.
const StatusPill = ({ ok, warn, text, testId }) => {
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  const color = ok ? "#22c55e" : warn ? "#f59e0b" : "#ff5577";
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold border"
      style={{ color, borderColor: `${color}55`, background: `${color}12` }}
    >
      <Icon size={10} /> {text}
    </span>
  );
};

const PushDiagnostic = () => {
  const { supported, permission, subscribed, status, busy, error, enable, disable, sendTest, refreshStatus } = useWebPush();
  const [lastTest, setLastTest] = useState(null);   // { at, response, error }
  const [envInfo, setEnvInfo] = useState({});

  useEffect(() => {
    setEnvInfo({
      origin: window.location.origin,
      ua: navigator.userAgent,
      standalone: window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true,
      cookies: navigator.cookieEnabled,
    });
  }, []);

  const runTest = async () => {
    const res = await sendTest();
    const at = new Date().toISOString();
    if (res) {
      setLastTest({ at, response: res, error: null });
      if (res.sent > 0) toast.success(`Test sent to ${res.sent} device${res.sent > 1 ? "s" : ""}.`);
      else if (res.skipped) toast.info("Skipped — check quiet hours + subscription origin.");
      else toast.error("Test call returned 0 devices. Subscribe first.");
    } else {
      setLastTest({ at, response: null, error: error || "unknown" });
      toast.error(error || "Test failed");
    }
  };

  const copyDiagnostic = async () => {
    const blob = {
      captured_at: new Date().toISOString(),
      supported, permission, subscribed,
      server_status: status,
      env: envInfo,
      last_test: lastTest,
      last_error: error,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(blob, null, 2));
      toast.success("Diagnostic copied to clipboard.");
    } catch { toast.error("Copy failed"); }
  };

  const iosButNotInstalled = /iPhone|iPad/i.test(envInfo.ua || "") && envInfo.standalone === false;

  return (
    <div className="space-y-4" data-testid="push-diagnostic">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium">Push notifications</div>
          <div className="text-xs text-white/50">
            Enable + test — if a ping doesn&apos;t land, hit &ldquo;Copy diagnostic&rdquo; and paste it back.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {subscribed ? (
            <button
              onClick={disable}
              disabled={busy}
              data-testid="push-disable"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.1] text-xs disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Disable on this device
            </button>
          ) : (
            <button
              onClick={enable}
              disabled={busy || !supported}
              data-testid="push-enable"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#22c55e] text-[#05050a] font-semibold text-xs hover:bg-white disabled:opacity-40"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Enable on this device
            </button>
          )}
          <button
            onClick={runTest}
            disabled={busy}
            data-testid="test-push-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-[#00f0ff]/15 hover:border-[#00f0ff]/40 hover:text-[#00f0ff] text-xs disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Send test
          </button>
        </div>
      </div>

      {/* Live state pills */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex flex-wrap gap-2 mb-3">
          <StatusPill ok={supported} text={supported ? "Browser supports push" : "Browser can't push"} testId="pill-supported" />
          <StatusPill
            ok={permission === "granted"}
            warn={permission === "default"}
            text={`Permission: ${permission}`}
            testId="pill-permission"
          />
          <StatusPill
            ok={subscribed}
            warn={!subscribed && permission === "granted"}
            text={subscribed ? "Subscribed on this device" : "Not subscribed here"}
            testId="pill-subscribed"
          />
          <StatusPill
            ok={(status?.count || 0) > 0}
            warn={(status?.count || 0) === 0}
            text={`Server sees ${status?.count ?? "?"} device(s)`}
            testId="pill-count"
          />
          {status?.in_quiet_hours_now ? (
            <StatusPill warn={true} text="In quiet hours (test still fires)" testId="pill-quiet" />
          ) : (
            <StatusPill ok text="Not in quiet hours" testId="pill-quiet" />
          )}
        </div>

        {iosButNotInstalled && (
          <div className="text-[11px] text-[#f59e0b] bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-lg p-2.5 mb-3">
            📱 iOS detected. iOS only exposes web push when the site is <b>Added to Home Screen</b>.
            Tap Share → &ldquo;Add to Home Screen&rdquo;, then open THAT icon and re-enable here.
          </div>
        )}
        {error && (
          <div className="text-[11px] text-[#ff5577] bg-[#ff0055]/10 border border-[#ff0055]/30 rounded-lg p-2.5 mb-3">
            Last error: <span className="font-mono">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[11px] font-mono">
          <div><span className="text-white/45">origin:</span> <span className="text-white/85 break-all">{envInfo.origin}</span></div>
          <div><span className="text-white/45">standalone (PWA):</span> {envInfo.standalone ? <span className="text-[#22c55e]">yes</span> : <span className="text-white/70">no</span>}</div>
          <div className="md:col-span-2"><span className="text-white/45">userAgent:</span> <span className="text-white/70 break-all">{envInfo.ua}</span></div>
          {lastTest && (
            <>
              <div><span className="text-white/45">last test at:</span> {lastTest.at?.slice(11, 19)}</div>
              <div>
                <span className="text-white/45">last test result:</span>{" "}
                <span className={lastTest.response?.sent > 0 ? "text-[#22c55e]" : "text-[#ff5577]"}>
                  {lastTest.response ? `sent=${lastTest.response.sent} failed=${lastTest.response.failed} skipped=${lastTest.response.skipped}` : lastTest.error}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            onClick={refreshStatus}
            data-testid="push-refresh-status"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-[11px] hover:bg-white/[0.1]"
          >
            <RefreshCw size={10} /> Refresh state
          </button>
          <button
            onClick={copyDiagnostic}
            data-testid="push-copy-diagnostic"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-[11px] hover:bg-white/[0.1]"
          >
            <Copy size={10} /> Copy diagnostic
          </button>
        </div>
      </div>
    </div>
  );
};
